import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Sparkles, Copy, AlertCircle } from 'lucide-react';
import { generateOutlineStreaming, checkTopicViability } from '../lib/ai-config';
import { parseBookOutline } from '../lib/outline-parser';

const outlineSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
  audience: z.string().optional(),
  goals: z.string().optional(),
  tone: z.string().optional(),
  targetWordCount: z.number().min(100).max(10000).optional(),
});

type OutlineFormData = z.infer<typeof outlineSchema>;

const bookTypeLabels: Record<string, string> = {
  authority: 'Authority / Business Book',
  educational: 'How-To / Educational Guide',
  fiction: 'Fiction Novel',
  ebook: 'Short eBook / Lead Magnet',
  children: "Children's Book",
};

interface OutlineGeneratorProps {
  onOutlineGenerated: (outline: string) => void;
  existingTitle?: string;
  bookType?: string;
  targetWordCount?: number;
}

export function OutlineGenerator({
  onOutlineGenerated,
  existingTitle,
  bookType,
  targetWordCount: parentTargetWordCount,
}: OutlineGeneratorProps) {
  const [isChecking, setIsChecking] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedOutline, setGeneratedOutline] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [viabilityRejection, setViabilityRejection] = useState<{
    reason: string;
    suggested_adjustments: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OutlineFormData>({
    resolver: zodResolver(outlineSchema),
    defaultValues: {
      title: existingTitle || '',
      targetWordCount: parentTargetWordCount || 1000,
    },
  });

  const generateBookOutline = async (data: OutlineFormData) => {
    setIsChecking(true);
    setError(null);
    setViabilityRejection(null);
    setGeneratedOutline('');

    try {
      // Step 1: Pre-flight viability check
      const viability = await checkTopicViability({
        title: data.title,
        description: data.description,
        audience: data.audience,
        targetWordCount: data.targetWordCount,
        bookType: bookType || 'authority',
      });

      if (!viability.viable) {
        setViabilityRejection({
          reason: viability.reason,
          suggested_adjustments: viability.suggested_adjustments,
        });
        setIsChecking(false);
        return;
      }

      // Step 2: Topic is viable — generate the outline
      setIsChecking(false);
      setIsGenerating(true);

      const outline = await generateOutlineStreaming(
        {
          ...data,
          bookType: bookType || 'authority',
        },
        (accumulated) => {
          setGeneratedOutline(accumulated);
        }
      );

      // Local quality check: ensure outline has chapters with substance
      const parsed = parseBookOutline(outline);
      if (parsed.chapters.length === 0) {
        throw new Error(
          'The generated outline does not contain any valid chapters. Please try again.'
        );
      }

      setGeneratedOutline(outline);
      onOutlineGenerated(outline);
    } catch (error) {
      console.error('Error generating outline:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate outline');
    } finally {
      setIsChecking(false);
      setIsGenerating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedOutline);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const isBusy = isChecking || isGenerating;

  return (
    <div className="space-y-6">
      {bookType && (
        <div className="bg-indigo-50 rounded-lg px-4 py-3 text-sm text-indigo-700">
          Generating outline for: <strong>{bookTypeLabels[bookType] || bookType}</strong>
        </div>
      )}

      <form onSubmit={handleSubmit(generateBookOutline)} className="space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
            Book Title
          </label>
          <input
            type="text"
            {...register('title')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
          {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>}
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            Book Description
          </label>
          <textarea
            {...register('description')}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="What is your book about? What are the main topics you want to cover?"
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="audience" className="block text-sm font-medium text-gray-700">
            Target Audience (Optional)
          </label>
          <input
            type="text"
            {...register('audience')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="Who is this book for?"
          />
        </div>

        <div>
          <label htmlFor="goals" className="block text-sm font-medium text-gray-700">
            Book Objectives (Optional)
          </label>
          <textarea
            {...register('goals')}
            rows={2}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="What should readers learn or achieve after reading your book?"
          />
        </div>

        <div>
          <label htmlFor="tone" className="block text-sm font-medium text-gray-700">
            Writing Tone (Optional)
          </label>
          <input
            type="text"
            {...register('tone')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="e.g., Professional, Conversational, Academic"
          />
        </div>

        <div>
          <label htmlFor="targetWordCount" className="block text-sm font-medium text-gray-700">
            Target Words per Chapter
          </label>
          <input
            type="number"
            {...register('targetWordCount', { valueAsNumber: true })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            min={100}
            max={10000}
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isBusy}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isChecking ? (
              <>
                <Loader2 className="animate-spin h-4 w-4 mr-2" />
                Checking topic viability...
              </>
            ) : isGenerating ? (
              <>
                <Loader2 className="animate-spin h-4 w-4 mr-2" />
                Generating outline...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Outline
              </>
            )}
          </button>
        </div>
      </form>

      {/* Topic viability rejection */}
      {viabilityRejection && (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">
                This topic may not produce quality content at the requested length
              </h3>
              <div className="mt-2 text-sm text-amber-700">
                <p>{viabilityRejection.reason}</p>
              </div>
              {viabilityRejection.suggested_adjustments && (
                <div className="mt-3 text-sm text-amber-700">
                  <p className="font-medium">Suggestions:</p>
                  <p className="mt-1">{viabilityRejection.suggested_adjustments}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Generation error */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {generatedOutline && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Generated Outline</h3>
            <button
              onClick={handleCopy}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Copy className="h-4 w-4 mr-2" />
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 prose max-w-none">
            <pre className="whitespace-pre-wrap text-sm font-sans">{generatedOutline}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
